import React, { useEffect, useMemo, useState } from 'react'
import { db, storage } from '../../../firebase'
import { collection, getDocs, orderBy, query } from 'firebase/firestore'
import { ref, getBlob } from 'firebase/storage'
import Icon from '../../../components/AppIcon'

const usd = (v) => {
  if (v == null || v === '') return '—'
  const n = Number(v)
  return isNaN(n) ? '—' : `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}
// gold_transactions exist in two shapes (migrated app records vs portal-created)
const txPayout = (t) => t.totalPayout ?? t.totals?.totalNet
const txWeight = (t) => t.totalWeightGrams ?? t.totals?.totalWeight
const txDesagio = (t) =>
  t.desagioPercent ??
  (t.totals?.totalGross ? Math.round((t.totals.totalDesagio / t.totals.totalGross) * 1000) / 10 : '—')
// Item rows: migrated {karat, weightGrams, pricePerGram, subtotal} vs portal
// {metal, karatWhole, karatDecimal, weight, itemType}
const itemView = (it) => {
  if (it.weightGrams != null || it.karat != null) {
    const isSilver = Number(it.karat) === 0
    return {
      label: `${isSilver ? 'Silver' : `${it.karatValue || it.karat}K`}${it.description ? ` · ${it.description}` : ''}`,
      weight: Number(it.weightGrams),
      pricePerGram: it.pricePerGram,
      subtotal: it.subtotal,
    }
  }
  const isSilver = it.metal === 'silver'
  return {
    label: `${isSilver ? 'Silver' : `${it.karatWhole}.${it.karatDecimal}K`}${it.itemType ? ` · ${it.itemType}` : ''}`,
    weight: Number(it.weight),
    pricePerGram: null,
    subtotal: null,
  }
}
const fmtDate = (val) => {
  try {
    const d = val?.toDate ? val.toDate() : new Date(val)
    return isNaN(d) ? '-' : d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
  } catch { return '-' }
}

function PhotoThumb({ path, label }) {
  const [url, setUrl] = useState(null)
  const [failed, setFailed] = useState(false)
  useEffect(() => {
    let alive = true
    let objectUrl = null
    getBlob(ref(storage, path))
      .then((blob) => { if (alive) { objectUrl = URL.createObjectURL(blob); setUrl(objectUrl) } })
      .catch(() => { if (alive) setFailed(true) })
    return () => { alive = false; if (objectUrl) URL.revokeObjectURL(objectUrl) }
  }, [path])

  return (
    <div className="space-y-1">
      <div className="aspect-[4/3] rounded-lg border border-border bg-muted/40 overflow-hidden flex items-center justify-center">
        {url ? (
          <img src={url} alt={label} className="w-full h-full object-contain" />
        ) : failed ? (
          <Icon name="ImageOff" size={20} className="text-muted-foreground" />
        ) : (
          <Icon name="Loader2" size={20} className="text-muted-foreground animate-spin" />
        )}
      </div>
      <p className="text-[11px] text-muted-foreground truncate">{label}</p>
    </div>
  )
}

function TransactionDetail({ tx, onClose }) {
  const c = tx.customer || {}
  return (
    <div className="fixed inset-0 z-[80] bg-black/40 p-4 flex items-center justify-center" onClick={onClose}>
      <div
        className="bg-card border border-border rounded-2xl shadow-elevation-4 w-full max-w-2xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between p-5 border-b border-border sticky top-0 bg-card">
          <div>
            <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
              {tx.transactionNumber}
              {tx.status === 'cancelled' && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-destructive/10 text-destructive text-xs font-semibold">
                  <Icon name="Ban" size={11} /> Cancelled
                </span>
              )}
            </h3>
            <p className="text-sm text-muted-foreground">{fmtDate(tx.createdAt)} · Operator: {tx.operator?.name || '—'} · Envelope: {tx.envelopeNumber || '—'}</p>
            {tx.status === 'cancelled' && (
              <p className="text-xs text-destructive mt-0.5">
                Cancelled {fmtDate(tx.cancelledAt?.toDate ? tx.cancelledAt.toDate() : tx.cancelledAt)}{tx.cancelledBy?.name ? ` by ${tx.cancelledBy.name}` : ''}{tx.cancelReason ? ` — ${tx.cancelReason}` : ''}
              </p>
            )}
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg text-muted-foreground hover:bg-muted/50" aria-label="Close">
            <Icon name="X" size={18} />
          </button>
        </div>

        <div className="p-5 space-y-5">
          {/* Customer */}
          <section>
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">Customer</p>
            <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
              <div><span className="text-muted-foreground">Name:</span> <span className="text-foreground">{c.name || '—'}</span></div>
              <div><span className="text-muted-foreground">Phone:</span> <span className="text-foreground">{c.phone || '—'}</span></div>
              <div><span className="text-muted-foreground">ID:</span> <span className="text-foreground">{c.idType || '—'} {c.idNumber || ''}</span></div>
              <div><span className="text-muted-foreground">Email:</span> <span className="text-foreground">{c.email || '—'}</span></div>
            </div>
          </section>

          {/* Items */}
          <section>
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">Items</p>
            <div className="overflow-x-auto rounded-lg border border-border">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 text-muted-foreground">
                  <tr>
                    <th className="text-left p-2 font-medium">Karat</th>
                    <th className="text-right p-2 font-medium">Weight (g)</th>
                    <th className="text-right p-2 font-medium">Price/g</th>
                    <th className="text-right p-2 font-medium">Subtotal</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {(tx.items || []).map((it, i) => {
                    const v = itemView(it)
                    return (
                      <tr key={i}>
                        <td className="p-2 text-foreground">{v.label}</td>
                        <td className="p-2 text-right text-foreground">{Number.isFinite(v.weight) ? v.weight.toFixed(3) : '—'}</td>
                        <td className="p-2 text-right text-foreground">{usd(v.pricePerGram)}</td>
                        <td className="p-2 text-right text-foreground">{usd(v.subtotal)}</td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
            <div className="flex justify-end gap-6 mt-3 text-sm">
              <span className="text-muted-foreground">Weight: <span className="text-foreground font-medium">{Number(txWeight(tx) || 0).toFixed(3)}g</span></span>
              <span className="text-muted-foreground">Deságio: <span className="text-foreground font-medium">{txDesagio(tx)}%</span></span>
              <span className="text-foreground font-semibold">Payout: {usd(txPayout(tx))}</span>
            </div>
          </section>

          {/* Photos & signatures — migrated docs carry Storage paths in `files`;
              portal docs carry download URLs in `captures` */}
          {(tx.files || []).length > 0 && (
            <section>
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">Photos & signatures</p>
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                {tx.files.map((f, i) => (
                  <PhotoThumb key={i} path={f.path} label={f.label || f.fileType} />
                ))}
              </div>
            </section>
          )}
          {!((tx.files || []).length > 0) && tx.captures && Object.keys(tx.captures).length > 0 && (
            <section>
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">Photos & signatures</p>
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                {Object.entries(tx.captures).filter(([, url]) => !!url).map(([key, url]) => (
                  <div key={key} className="space-y-1">
                    <div className="aspect-[4/3] rounded-lg border border-border bg-muted/40 overflow-hidden flex items-center justify-center">
                      <img src={url} alt={key} className="w-full h-full object-contain" />
                    </div>
                    <p className="text-[11px] text-muted-foreground truncate">{key.replace(/_/g, ' ')}</p>
                  </div>
                ))}
              </div>
            </section>
          )}
        </div>
      </div>
    </div>
  )
}

export default function ExchangeTransactions() {
  const [rows, setRows] = useState(null)
  const [error, setError] = useState('')
  const [search, setSearch] = useState('')
  const [selected, setSelected] = useState(null)

  useEffect(() => {
    let alive = true
    ;(async () => {
      try {
        const snap = await getDocs(query(collection(db, 'gold_transactions'), orderBy('createdAt', 'desc')))
        if (alive) setRows(snap.docs.map((d) => ({ id: d.id, ...d.data() })))
      } catch (e) {
        if (alive) setError(e?.message || 'Failed to load transactions')
      }
    })()
    return () => { alive = false }
  }, [])

  const filtered = useMemo(() => {
    if (!rows) return []
    const q = search.trim().toLowerCase()
    if (!q) return rows
    return rows.filter((t) =>
      (t.transactionNumber || '').toLowerCase().includes(q) ||
      (t.customer?.name || '').toLowerCase().includes(q) ||
      (t.envelopeNumber || '').toLowerCase().includes(q)
    )
  }, [rows, search])

  return (
    <div className="bg-card rounded-2xl border border-border p-5 sm:p-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <div>
          <h2 className="text-lg font-semibold text-foreground">Transactions</h2>
          <p className="text-sm text-muted-foreground">{rows ? `${rows.length} recorded purchases` : 'Loading…'}</p>
        </div>
        <div className="relative w-full sm:w-72">
          <Icon name="Search" size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search number, customer, envelope"
            className="w-full h-9 pl-9 pr-3 rounded-lg border border-border bg-input text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
          />
        </div>
      </div>

      {error ? (
        <div className="p-4 rounded-lg bg-destructive/10 border border-destructive/25 text-sm text-destructive">{error}</div>
      ) : !rows ? (
        <div className="p-8 text-center text-muted-foreground text-sm">Loading transactions…</div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-border">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-muted-foreground">
              <tr>
                <th className="text-left p-3 font-medium">Number</th>
                <th className="text-left p-3 font-medium">Date</th>
                <th className="text-left p-3 font-medium">Customer</th>
                <th className="text-left p-3 font-medium">Operator</th>
                <th className="text-left p-3 font-medium">Envelope</th>
                <th className="text-right p-3 font-medium">Payout</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filtered.map((t) => (
                <tr key={t.id} className={`hover:bg-muted/40 cursor-pointer transition-colors ${t.status === 'cancelled' ? 'opacity-60' : ''}`} onClick={() => setSelected(t)}>
                  <td className="p-3 font-medium text-foreground">
                    {t.transactionNumber}
                    {t.status === 'cancelled' && (
                      <span className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-destructive/10 text-destructive text-xs font-semibold">
                        <Icon name="Ban" size={11} /> Cancelled
                      </span>
                    )}
                  </td>
                  <td className="p-3 text-muted-foreground">{fmtDate(t.createdAt)}</td>
                  <td className="p-3 text-foreground">{t.customer?.name || '—'}</td>
                  <td className="p-3 text-muted-foreground">{t.operator?.name || '—'}</td>
                  <td className="p-3 text-muted-foreground">{t.envelopeNumber || '—'}</td>
                  <td className="p-3 text-right font-semibold text-foreground">{usd(txPayout(t))}</td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={6} className="p-8 text-center text-muted-foreground">No transactions match “{search}”.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {selected && <TransactionDetail tx={selected} onClose={() => setSelected(null)} />}
    </div>
  )
}
